import React, { useCallback, MouseEventHandler, FormEventHandler } from 'react';
import { Combobox, FormField, Fieldset } from '@vwfs-bronson/bronson-react';
import { InfoIcon } from '../../info-icon';
import { useField } from 'formik';
import './ValidatedCombobox.css';
import { getInputErrorMessageComponentAndId, hasError } from '../errors';

export type ComboboxOption = { key: string; value: string };

export type ComboboxState = {
    value: ComboboxOption[];
};

export type ComboboxItem = {
    /**
     * The unique identifier of the element
     */
    key: string;
    /**
     * The text which is shown when item is selected and the Combobox is closed
     */
    value: string;
    disabled?: boolean;
};

export type ValidatedComboboxProps = {
    name: string;
    testId?: string;
    options: ComboboxItem[];
    label?: string;
    id?: string;
    /**
     * ComboboxInternalProps
     */
    className?: string;
    /**
     * The optionKey of the element which is preselected
     */
    defaultOptionKeys?: string[];
    disabled?: boolean;
    multiple?: boolean;
    onChange?: () => void;
    onKeyDown?: () => void;
    onListClose?: () => void;
    onListOpen?: () => void;
    defaultOpen?: boolean;
    placeholder?: string;
    valueFormatter?: (values: ComboboxOption[]) => string;
    readOnly?: boolean;
    searchable?: boolean;
    /**
     * ComboboxItem Props
     */
    divProps?: { [key: string]: string | boolean | (() => void) };
    handle?: string;
    hint?: string;
    onLinkClick?: MouseEventHandler;
    tooltip?: string | React.ReactNode;
    handleChange?: FormEventHandler<HTMLElement>;
    isMandatory?: boolean;
    withHtmlValidationWarning?: boolean;
};

// This component makes some hacky workarounds. We need to align with Bronson and Bronson React to implement some changes here
export const ValidatedCombobox: React.FC<ValidatedComboboxProps> = ({
    name,
    testId,
    options,
    label,
    defaultOptionKeys,
    disabled,
    placeholder,
    onChange,
    onKeyDown,
    onListClose,
    onListOpen,
    searchable,
    defaultOpen,
    divProps,
    onLinkClick,
    hint,
    id,
    tooltip,
    isMandatory,
    className,
    withHtmlValidationWarning,
    multiple,
    valueFormatter,
    readOnly,
}) => {
    const [, meta, helpers] = useField({ name });
    const { setValue, setTouched } = helpers;
    if (multiple && !valueFormatter) {
        console.error('valueFormatter prop function should be provided for multiple Combobox');
    }

    const getDefaultState = (): ComboboxState => {
        return {
            value: [
                ...(defaultOptionKeys || []).map(defaultKey => {
                    return {
                        value: options.find(({ key }) => key === defaultKey)?.value || '',
                        key: defaultKey,
                    };
                }),
            ],
        };
    };

    const internalOnChange = useCallback((options: ComboboxOption[]): void => {
        const values: string[] = options.map(option => option.key);
        setValue(values);
        setTouched(true, true);
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        onChange && onChange();
    }, []);

    const infoIcon = tooltip ? <InfoIcon text={tooltip} /> : null;

    const { errorMessageComponent, errorMessageId } = getInputErrorMessageComponentAndId({
        name,
        id,
        meta,
        testId,
        withHtmlValidationWarning,
    });

    return (
        <Fieldset>
            <Fieldset.Row>
                <FormField
                    type="select"
                    onClick={onLinkClick}
                    hint={hint}
                    labelText={label}
                    testId={`${testId}FormField`}
                    id={id}
                    errorMessage={errorMessageComponent}
                    infoIcon={infoIcon}
                    required={isMandatory}
                    className={className}
                >
                    <Combobox
                        error={!disabled && hasError(meta)}
                        testId={`${testId}Combobox`}
                        onChange={internalOnChange}
                        onKeyDown={onKeyDown}
                        onListClose={onListClose}
                        onListOpen={onListOpen}
                        disabled={disabled}
                        placeholder={placeholder}
                        defaultState={getDefaultState()}
                        style={{ cursor: 'pointer' }}
                        searchable={searchable}
                        open={defaultOpen}
                        multiple={multiple}
                        valueFormatter={valueFormatter}
                        readOnly={readOnly}
                        options={options}
                        aria-describedby={errorMessageId}
                        {...divProps}
                    />
                </FormField>
            </Fieldset.Row>
        </Fieldset>
    );
};
